import {
  afterNextRender,
  DestroyRef,
  effect,
  inject,
  Injectable,
  Injector,
  runInInjectionContext,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs';
import { GBAttribute, GrowthBookService } from '@lentau/core/growth-book/shared';
import { GrowthBookAttributesService } from '@lentau/core/growth-book/shared/growth-book-attributes.service';
import { KppFactory } from './kpp.factory';
import { BusinessTypeFactory } from './business-type.factory';
import { WindowEventsService } from '@lentau/ui-kit/core/services/window-events/window-events.service';
import { getGrowthBookPlatformId } from '@lentau/core/growth-book/shared';
import { GrowthBookDeviceOs } from '@lentau/core/growth-book/shared';
import { AnalyticsService } from '@lentau/analytics/events/shared/services/analytics.service';
import { getGBInitEvent } from './growth-book.analytic';
import { RenderTimeService } from '@lentau/core/measure';
import { UserFacade } from '@lentau/profile/store/user/user.facade';
import { GrowthBookExperimentsService } from './growth-book.experiments';
import { GrowthBookRequestBuilder } from '@lentau/core/growth-book/shared/growth-book-request.builder';

@Injectable({
  providedIn: 'root',
})
export class InitGrowthBookService {
  private destroyRef: DestroyRef = inject(DestroyRef);
  private growthBookService: GrowthBookService = inject(GrowthBookService);
  private growthBookExperiments: GrowthBookExperimentsService = inject(GrowthBookExperimentsService);
  private analyticsService: AnalyticsService = inject(AnalyticsService);
  private renderTimeService: RenderTimeService = inject(RenderTimeService);
  private userFacade: UserFacade = inject(UserFacade);
  private requestBuilder: GrowthBookRequestBuilder = inject(GrowthBookRequestBuilder);
  private growthBookAttributesService: GrowthBookAttributesService = inject(GrowthBookAttributesService);
  private kppFactory: KppFactory = inject(KppFactory);
  private businessTypeFactory: BusinessTypeFactory = inject(BusinessTypeFactory);
  private windowEventsService: WindowEventsService = inject(WindowEventsService);
  private injector: Injector = inject(Injector);

  public init(): void {
    afterNextRender(() => {
      runInInjectionContext(this.injector, () => {
        this.subscribeToUserChanges();

        this.requestBuilder.requestAllowedExperiments();

        this.growthBookExperiments.setAnalyticsByExperiments();

        this.setGBInitAnalytics();
      });
    });
  }

  private setGBInitAnalytics(): void {
    this.growthBookService.isInitialized$
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.analyticsService.pushToDataLayer(getGBInitEvent(this.renderTimeService.getTime())));
  }

  private subscribeToUserChanges(): void {
    effect(() => {
      this.userFacade.passportId();
      const segments: string[] | undefined = this.userFacade.segments();
      const safeSegments: string[] = segments ?? [];

      untracked(() => {
        const attributes: GBAttribute = this.growthBookAttributesService.buildAttributes({
          segments: safeSegments,
          passportId: this.userFacade.passportId() || '',
          kpp: this.kppFactory.get(),
          businessType: this.businessTypeFactory.get(),
          deviceOs: this.windowEventsService.isDesktop() ? GrowthBookDeviceOs.WEB : GrowthBookDeviceOs.MOBILE,
          platformId: getGrowthBookPlatformId(),
          legalEntityId: this.userFacade.legalEntityId(),
        });

        this.growthBookService.setGrowBook(attributes);
      });
    });
  }
}
